import React, { createContext, useContext, useReducer, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { authReducer, initialAuthState, AUTH_ACTIONS } from './authReducer';
import * as authApi from '../api/authApi';
import * as tokenStorage from '../../api/tokenStorage';
import { setOnAuthFailure } from '../../api/apiClient';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialAuthState);
  const navigate = useNavigate();

  const forceLogout = useCallback(() => {
    tokenStorage.clearAllTokens();
    dispatch({ type: AUTH_ACTIONS.LOGOUT });
    navigate('/auth/login');
  }, [navigate]);

  useEffect(() => {
    setOnAuthFailure(forceLogout);
  }, [forceLogout]);

  useEffect(() => {
    let cancelled = false;

    async function rehydrateSession() {
      const refreshToken = tokenStorage.getRefreshToken();

      if (!refreshToken) {
        dispatch({
          type: AUTH_ACTIONS.SET_INITIALIZING,
          payload: { isInitializing: false },
        });
        return;
      }

      try {
        const tokenData = await authApi.refreshTokens(refreshToken);
        tokenStorage.setTokens(tokenData.accessToken, tokenData.refreshToken, tokenData.expiresIn);

        const userData = await authApi.getUser();

        if (!cancelled) {
          dispatch({
            type: AUTH_ACTIONS.LOGIN,
            payload: { user: userData },
          });
        }
      } catch (_error) {
        // Multi-tab race guard: another tab may have concurrently refreshed the same
        // token. Wait briefly so it has time to write the new refresh token to localStorage,
        // then retry once with the updated token before giving up.
        await new Promise((r) => setTimeout(r, 100));
        if (cancelled) return;

        const freshToken = tokenStorage.getRefreshToken();
        if (freshToken && freshToken !== refreshToken) {
          try {
            const tokenData = await authApi.refreshTokens(freshToken);
            tokenStorage.setTokens(tokenData.accessToken, tokenData.refreshToken, tokenData.expiresIn);
            const userData = await authApi.getUser();
            if (!cancelled) {
              dispatch({ type: AUTH_ACTIONS.LOGIN, payload: { user: userData } });
            }
            return;
          } catch {
            // Retry also failed; fall through to full logout.
          }
        }

        tokenStorage.clearAllTokens();
        if (!cancelled) {
          dispatch({
            type: AUTH_ACTIONS.SET_INITIALIZING,
            payload: { isInitializing: false },
          });
        }
      }
    }

    rehydrateSession();

    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email, password) => {
    const tokenData = await authApi.login(email, password);
    tokenStorage.setTokens(tokenData.accessToken, tokenData.refreshToken, tokenData.expiresIn);
    const userData = await authApi.getUser();
    dispatch({ type: AUTH_ACTIONS.LOGIN, payload: { user: userData } });
  }, []);

  const loginWithGoogle = useCallback(async (idToken) => {
    const tokenData = await authApi.loginWithGoogle(idToken);
    tokenStorage.setTokens(tokenData.accessToken, tokenData.refreshToken, tokenData.expiresIn);
    const userData = await authApi.getUser();
    dispatch({ type: AUTH_ACTIONS.LOGIN, payload: { user: userData } });
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = tokenStorage.getRefreshToken();
    tokenStorage.clearAllTokens();
    dispatch({ type: AUTH_ACTIONS.LOGOUT });
    authApi.logout(refreshToken).catch(() => {});
    navigate('/auth/login');
  }, [navigate]);

  const updateUser = useCallback((user) => {
    dispatch({ type: AUTH_ACTIONS.SET_USER, payload: { user } });
  }, []);

  const value = useMemo(() => ({
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isInitializing: state.isInitializing,
    login,
    loginWithGoogle,
    logout,
    updateUser,
  }), [state.user, state.isAuthenticated, state.isInitializing, login, loginWithGoogle, logout, updateUser]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
